import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';

import useBodyClassName from 'hooks/useBodyClassName';
import { Button } from 'components/core/Button';
import { countAppliedFilters } from 'utils/search';
import { getKeywordConfig } from 'config/nearMeKeywords';
import {
	getFilterValueForAmenity,
	getSqftParamsForSizeSlug,
} from 'utils/search/parseAmenityFromPath';
import { INTERNAL_PAGE_TYPES } from 'config/constants';
import './SearchFiltersTray.scss';
import SearchFiltersVariation from '../SearchFiltersVariation/SearchFiltersVariation';

/**
 * Base search param keys that survive a "Clear All".
 * Everything else (minPrice, maxPrice, hasSpecial, averageReview, etc.)
 * is explicitly set to undefined so the upstream pickBy spread in
 * onSearch properly strips old filter values.
 */
const BASE_PARAM_KEYS = [
	'location',
	'type',
	'city',
	'state',
	'latitude',
	'longitude',
	'zip',
	'subPageType',
	'vehicle',
	'pageType',
	'sort',
	'dealsFilter',
];

/**
 * Subset of BASE_PARAM_KEYS whose Clear All value is restored from the
 * initial mount snapshot rather than the current filterParams value.
 * Used for params that filter handlers mutate during a session but that
 * must revert to the navigation-level value on reset (SF-20339).
 */
const RESTORE_TO_INITIAL_KEYS = new Set(['type']);

/**
 * Returns the number of filters that the page itself injects via subPageType
 * (NMP/ALP keyword amenity or size slug). These are page-level defaults, not
 * user-applied filters, so they should not count toward enabling Clear All.
 */
function getPageBaselineFilterCount(params) {
	if (!params?.subPageType) return 0;

	let count = 0;
	const filterValue = getFilterValueForAmenity(params.subPageType);
	if (filterValue && params[filterValue]) count += 1;

	// SIZE_SLUG_TO_SQFT uses CLP slugs ('mini-self-storage'), but NMP subPageType
	// uses short slugs ('mini'). Try direct lookup first, then resolve via clpSlug.
	let sqftParams = getSqftParamsForSizeSlug(params.subPageType);
	if (!sqftParams) {
		const keywordConfig = getKeywordConfig(params.subPageType);
		if (keywordConfig?.clpSlug) {
			sqftParams = getSqftParamsForSizeSlug(keywordConfig.clpSlug);
		}
	}
	if (sqftParams) {
		if (sqftParams.sqft && params.sqft === sqftParams.sqft) count += 1;
		if (sqftParams.onlyLocker && params.onlyLocker) count += 1;
	}

	return count;
}

export const enhance = (Wrapped) => {
	function EnhancedSearchFiltersTray(props) {
		const { searchParams, onFilter, closeFilters } = props;
		const [filterParams, setFilterParams] = useState(searchParams);

		// Snapshot of RESTORE_TO_INITIAL_KEYS values at mount.
		// Refreshed when page navigates to a different search context (SF-20339).
		const initialSnapshotRef = useRef(
			Object.fromEntries(
				[...RESTORE_TO_INITIAL_KEYS].map((key) => [
					key,
					searchParams?.[key],
				]),
			),
		);

		// Keep a ref of the latest filterParams so callbacks can read
		// fresh state without needing to be re-created on every change.
		const filterParamsRef = useRef(filterParams);
		filterParamsRef.current = filterParams;

		// Sync filterParams when searchParams prop changes externally
		// (replaces recompose lifecycle.componentDidUpdate).
		const prevSearchParamsRef = useRef(searchParams);
		useEffect(() => {
			if (!isEqual(prevSearchParamsRef.current, searchParams)) {
				setFilterParams(searchParams);

				// Reset initialSnapshot when the page navigates to a different search
				// context (e.g. storage CLP → vehicle CLP via client-side routing).
				// Uses composite (pageType, city, state, vehicle) as the navigation
				// identity — these fields are preserved across filter searches but
				// change on genuine page navigation (SF-20339).
				const navKey = (sp) =>
					`${sp?.pageType}:${sp?.city}:${sp?.state}:${sp?.vehicle}`;
				if (
					navKey(prevSearchParamsRef.current) !== navKey(searchParams)
				) {
					initialSnapshotRef.current = Object.fromEntries(
						[...RESTORE_TO_INITIAL_KEYS].map((key) => [
							key,
							searchParams?.[key],
						]),
					);
				}
			}
			prevSearchParamsRef.current = searchParams;
		}, [searchParams]);

		const handleQueryParamChange = useCallback((params) => {
			// SSDR: Always stage filters for Apply button (both mobile and desktop)
			// Merge new params with existing filterParams (don't replace)
			setFilterParams((prev) => ({ ...prev, ...params }));
		}, []);

		const handleApplyFilters = useCallback(() => {
			const current = filterParamsRef.current;
			// Set filtered flag based on whether any filters are applied
			// Gateway uses this to determine whether to generate dynamic SEO content
			const hasFilters = countAppliedFilters(current) > 0;
			onFilter({
				...current,
				filtered: hasFilters ? true : undefined,
			});

			// Close tray and apply filters
			if (closeFilters) {
				closeFilters();
			}
		}, [onFilter, closeFilters]);

		const handleClearAllFilters = useCallback(() => {
			// Union of filterParams and searchParams ensures we clear all
			// filter keys even if URL changed externally while tray was open.
			// The explicit undefined is critical — when handleApplyFilters
			// calls onSearch, the upstream merge does:
			//   pickBy({ ...searchParams, ...filterValues })
			// If filter keys are simply absent, the old searchParams values
			// survive. Explicit undefined overwrites them, then pickBy strips.
			const current = filterParamsRef.current;
			const allKeys = new Set([
				...Object.keys(current),
				...Object.keys(searchParams || {}),
			]);

			const clearedParams = {};
			allKeys.forEach((key) => {
				if (!BASE_PARAM_KEYS.includes(key)) {
					clearedParams[key] = undefined;
				} else if (RESTORE_TO_INITIAL_KEYS.has(key)) {
					// Restore from mount snapshot — see RESTORE_TO_INITIAL_KEYS.
					clearedParams[key] = initialSnapshotRef.current[key];
				} else {
					clearedParams[key] = current[key];
				}
			});

			if (
				clearedParams.pageType ===
					INTERNAL_PAGE_TYPES.NEAR_ME_SEARCH_PAGE &&
				clearedParams.subPageType
			) {
				const keywordConfig = getKeywordConfig(
					clearedParams.subPageType,
				);
				if (keywordConfig?.searchParams) {
					Object.assign(clearedParams, keywordConfig.searchParams);
					if (keywordConfig.category !== 'vehicle') {
						clearedParams.type = keywordConfig.searchParams.type;
						clearedParams.searchType =
							keywordConfig.searchParams.searchType;
					}
				}
			}

			setFilterParams(clearedParams);
		}, [searchParams]);

		/**
		 * Discards staged filter changes and closes the tray.
		 * Called when user closes tray without clicking "Apply Filters".
		 * Resets filterParams to searchParams so unapplied selections
		 * don't persist when the tray is reopened (SF-19289).
		 */
		const handleCloseWithoutApply = useCallback(() => {
			if (closeFilters) {
				closeFilters();
			}
			// Reset staged filters to match currently applied search params
			setFilterParams(searchParams);
		}, [closeFilters, searchParams]);

		const filtersHaveChanged = !isEqual(filterParams, searchParams);

		// Clear All is actionable when:
		// 1. Staged filterParams has user filters beyond the page baseline
		//    (NMP/ALP subPageType injects boolean params like driveUp:true
		//    that are not user choices and should not count).
		// 2. The URL carries user-applied filters (filtered=true) AND there
		//    are still filters visible in the staged state (count > 0).
		//    Using count>0 instead of !filtersHaveChanged so partial
		//    uncheck scenarios (e.g. user unchecks 2 of 3 filters, baseline
		//    remains) still enable Clear All while naturally disabling it
		//    after Clear All is clicked (count drops to 0).
		const baselineFilterCount = getPageBaselineFilterCount(filterParams);
		const canClearAll =
			countAppliedFilters(filterParams) > baselineFilterCount ||
			(!!searchParams?.filtered && countAppliedFilters(filterParams) > 0);

		return (
			<Wrapped
				{...props}
				filterParams={filterParams}
				filtersHaveChanged={filtersHaveChanged}
				canClearAll={canClearAll}
				handleQueryParamChange={handleQueryParamChange}
				handleApplyFilters={handleApplyFilters}
				handleClearAllFilters={handleClearAllFilters}
				handleCloseWithoutApply={handleCloseWithoutApply}
			/>
		);
	}

	EnhancedSearchFiltersTray.propTypes = {
		searchParams: PropTypes.object,
		onFilter: PropTypes.func,
		closeFilters: PropTypes.func,
	};

	return EnhancedSearchFiltersTray;
};

export function SearchFiltersTray({
	filtersShowing,
	handleQueryParamChange,
	filterParams,
	filtersHaveChanged,
	canClearAll,
	handleApplyFilters,
	handleClearAllFilters,
	handleCloseWithoutApply,
	searchType,
	useNewSearch,
	// unitFilters, // TODO: wire up for size/amenity filter sections
	facetCounts,
	className,
	idPrefix = '', // Unique prefix for element IDs to avoid conflicts with multiple instances
}) {
	// Prevent body scroll when mobile tray is open via CSS class (matches Modal pattern)
	// Desktop dropdowns allow background scrolling; only lock scroll for mobile
	const isMobileTray = className === 'mobile-filter-tray';
	const bodyClassName = classnames({
		'no-scroll': filtersShowing && isMobileTray,
	});
	useBodyClassName(bodyClassName);

	return (
		<>
			{/* Backdrop overlay - dark on mobile, transparent on desktop for click-outside */}
			{filtersShowing && (
				<button
					type="button"
					className={classnames('filters-tray-backdrop', className)}
					onClick={handleCloseWithoutApply}
					aria-label="Close filters"
				/>
			)}
			<div
				className={classnames('search-filters-tray', className, {
					open: filtersShowing,
					'new-search': useNewSearch,
				})}
			>
				<div className="filters-tray-content">
					<div className="modal-header">
						<h2 className="modal-title">Filters</h2>

						<Button
							type="button"
							className="clear-all-button"
							link={true}
							gray={true}
							onClick={handleClearAllFilters}
							underline={true}
							disabled={!canClearAll}
						>
							Clear All
						</Button>

						{handleCloseWithoutApply && (
							<button
								onClick={handleCloseWithoutApply}
								className="close-button"
								type="button"
								aria-label="Close filters"
							>
								×
							</button>
						)}
					</div>

					<SearchFiltersVariation
						queryParams={filterParams}
						onFilter={handleQueryParamChange}
						facetCounts={facetCounts}
						searchType={searchType}
						idPrefix={idPrefix}
					/>

					<div className="filters-tray-footer">
						<Button
							onClick={handleApplyFilters}
							primary={!useNewSearch}
							secondary={useNewSearch}
							className="apply-filters-button"
							disabled={!filtersHaveChanged}
						>
							Apply Filters
						</Button>
					</div>
				</div>
			</div>
		</>
	);
}

SearchFiltersTray.propTypes = {
	filtersShowing: PropTypes.bool,
	filterParams: PropTypes.object,
	filtersHaveChanged: PropTypes.bool,
	canClearAll: PropTypes.bool,
	handleQueryParamChange: PropTypes.func,
	handleApplyFilters: PropTypes.func.isRequired,
	handleClearAllFilters: PropTypes.func.isRequired,
	handleCloseWithoutApply: PropTypes.func.isRequired,
	searchType: PropTypes.string,
	useNewSearch: PropTypes.bool,
	searchParams: PropTypes.object,
	onFilter: PropTypes.func,
	facetCounts: PropTypes.object,
	className: PropTypes.string,
	idPrefix: PropTypes.string,
};

export default enhance(SearchFiltersTray);
